import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Box, Card, IconButton, InputAdornment, TextField, useMediaQuery } from '@mui/material'
import { Column, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { IconSearch, IconArrowRight } from '@tabler/icons-react'

import { ProposalStatus, TableApiV2, TableHeaderApi, ContentTitle, FilterButton } from '~/components'
import { FilterProposalDrawer } from './components'

import { SearchQueysData, useDebounce, usePermission, useQueryString, useToggle } from '~/hooks'
import { displayCpfCnpj, formatDate, ifIsDocumentReturnFormated, theme } from '~/utils'
import { useProposalListQuery } from '~/graphql/types'

const queryKeys: SearchQueysData[] = [
  { key: 'status', defaultValue: '', parseValue: true },
  {
    key: 'pendingDocs', defaultValue: '', parseValue: true
  },
  {
    key: 'inPendenciesStatus', defaultValue: '', parseValue: true
  },
  { key: 'name', defaultValue: '' },
  { key: 'begin', defaultValue: '' },
  { key: 'end', defaultValue: '' },
]

export const ProposalListAnalysis: React.FC = () => {
  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle(false)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const { searchQuerys, paginationQuerySize, searchParams, setSearchParams, page, size } = useQueryString()

  const queryValues = searchQuerys(queryKeys)

  const canEditProposals = usePermission(['proposals.create', 'proposals.updated'])
  const isLowerlg = useMediaQuery(theme.breakpoints.down('xl'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [filterName, setFilterName] = useState(queryValues.name)

  const debouncedFilterName = useDebounce(filterName, 1000)

  useEffect(() => {
    setSearchParams(state => {
      if (debouncedFilterName) {
        state.set('name', debouncedFilterName)
        state.set('page', '0')
      } else {
        state.delete('name')
      }
      return state
    })
  }, [debouncedFilterName])

  // Redirect focus back to a visible control when the filter drawer closes
  useEffect(() => {
    if (!drawerIsOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus())
    }
  }, [drawerIsOpen])

  const { data, loading } = useProposalListQuery({
    variables: {
      params: {
        pageSize: size,
        pageNumber: page,
        sort: {
          field: 'createdAt',
          order: -1
        },
        ...queryValues?.name?.length > 0 ? { nameOrDocument: ifIsDocumentReturnFormated(queryValues.name) } : {},
        ...queryValues?.status?.length > 0 ? { inStatus: queryValues.status } : {},
        // ...queryValues?.pendencies?.length > 0 ? { inPendencies: queryValues.pendencies } : {},
        ...queryValues?.pendingDocs?.length > 0 ? { inPendenciesStatus: queryValues.pendingDocs } : {},
        ...queryValues?.inPendenciesStatus?.length > 0 ? { inPendenciesStatus: queryValues.inPendenciesStatus } : {},
        ...queryValues?.begin && queryValues?.end ?
          {
            dateRange:
            {
              startDate: new Date(queryValues.begin),
              endDate: new Date(`${queryValues.end}Z23:59:59`)
            }
          }
          : {},
      }
    },
    onCompleted(result) {
      setSearchParams(state => {
        state.set('items', String(result.proposalList.meta.documentCounts))
        return state
      })
    },
    fetchPolicy: 'no-cache'
  })
  
  const linkToProposal = queryValues.inPendenciesStatus.length > 0

  const columns = useMemo((): Column[] => {
    return [
      {
        Header: 'Cliente',
        accessor: 'customerRef.name',
      },
      {
        Header: 'Tipo de pessoa',
        accessor: 'customerRef.personType',
      },
      {
        Header: 'CPF/CNPJ',
        accessor: 'customerRef.document',
        Cell: (props) => <>{displayCpfCnpj(props.value)}</>,
        disableSortBy: true,
        filter: 'includes'
      },
      {
        Header: 'Criação',
        accessor: 'createdAt',
        Cell: (props) => <>{formatDate(props.value, { dateHour: true })}</>
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: (props) => <ProposalStatus status={props.value} />
      },
      {
        Header: 'Ações',
        accessor: '_id',
        disableSortBy: true,
        Cell: ({ value }) => (
          <>
            {canEditProposals ? (
              <Link to={linkToProposal ? `/app/proposals/${value}` : `/app/proposals/${value}/pendencies`}>
                <IconButton>
                  <IconArrowRight />
                </IconButton>
              </Link>
            ) : (
              <IconButton disabled={true}>
                <IconArrowRight />
              </IconButton>
            )}
          </>
        ),
      },
    ]
  }, [linkToProposal])

  const dataWithMemo = useMemo(() => data?.proposalList?.data || [], [data])

  const defaultColumnHiddens = isLowerlg ? ['createdAt'].concat(isLowerMd ? [] : '').concat(isLowerSm ? ['customerRef.document'] : '') : []

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    defaultColumn: {
      Filter: <></>
    },
    manualPagination: true,
    initialState: {
      hiddenColumns: [...defaultColumnHiddens, 'customerRef.personType']
    },
  },
  useFilters,
  useSortBy,
  usePagination,
  useRowSelect,
  )

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title='Analisar documentos'
          description='Analisar documentos de propostas'
          breadcrumbLinks={{ currentLink: 'Propostas', links: [{ href: '/app/proposals', label: 'Vendas' }] }}
        />
        <TableHeaderApi
          startElement={
            (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%', flexFlow: isLowerSm ? 'column-reverse' : 'row' }}>
                <TextField
                  fullWidth
                  sx={{ maxWidth: '400px' }}
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  inputRef={searchInputRef}
                  placeholder='Procurar proposta por nome ou CPF/CNPJ'
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position='end'>
                          <IconSearch />
                        </InputAdornment>
                      </>
                    )
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <FilterButton onCustomClear={() => setFilterName('')} filtersToClear={queryKeys} toggleDrawer={toggleDrawer} filterCounter={searchParams.size - paginationQuerySize} />
                </Box>

                <FilterProposalDrawer queryKeys={queryKeys} drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} toggleDrawer={toggleDrawer} />
              </Box>
            )
          }
        />
        <TableApiV2
          isLoading={loading}
          tableMethods={tableMethods}
          columns={columns}
        />
      </Card>
    </div>
  )
}
